"use client";

import {
  Button,
  createSortableColumn,
  DataTable,
  Skeleton,
  type TanstackReactTable,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  AlertTriangleIcon,
  BanknoteIcon,
  EyeIcon,
  LoaderIcon,
  PlusIcon,
  WalletIcon,
  XIcon,
} from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { formatDateTime } from "@/shared/lib/date-time";
import { formatPrice } from "@/shared/lib/format-price";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { RowActions } from "@/shared/ui/RowActions";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { SummaryCard } from "@/shared/ui/SummaryCard";
import type { PayoutRequestItem } from "../api/payout-client";
import { usePayoutListQuery, usePayoutStatsQuery } from "../api/payout-queries";

const STATUS_OPTIONS = [
  { value: "all", label: "ສະຖານະທັງໝົດ" },
  { value: "requested", label: "ລໍຖ້າອະນຸມັດ" },
  { value: "approved", label: "ອະນຸມັດແລ້ວ" },
  { value: "processing", label: "ກຳລັງດຳເນີນການ" },
  { value: "paid", label: "ຈ່າຍແລ້ວ" },
  { value: "failed", label: "ລົ້ມເຫຼວ" },
  { value: "rejected", label: "ປະຕິເສດ" },
] as const;

const METHOD_OPTIONS = [
  { value: "all", label: "ວິທີທັງໝົດ" },
  { value: "bank_transfer", label: "ໂອນທະນາຄານ" },
  { value: "cash", label: "ເງິນສົດ" },
] as const;

const STATUS_STYLE: Record<
  string,
  { dot: string; text: string; label: string }
> = {
  requested: {
    dot: "bg-primary",
    text: "text-primary",
    label: "ລໍຖ້າອະນຸມັດ",
  },
  approved: {
    dot: "bg-secondary",
    text: "text-secondary",
    label: "ອະນຸມັດແລ້ວ",
  },
  processing: {
    dot: "bg-primary",
    text: "text-primary",
    label: "ກຳລັງດຳເນີນການ",
  },
  paid: {
    dot: "bg-success",
    text: "text-success",
    label: "ຈ່າຍແລ້ວ",
  },
  failed: {
    dot: "bg-destructive",
    text: "text-destructive",
    label: "ລົ້ມເຫຼວ",
  },
  rejected: {
    dot: "bg-destructive",
    text: "text-destructive",
    label: "ປະຕິເສດ",
  },
};

const OWNER_TYPE_LABELS: Record<string, string> = {
  customer: "ລູກຄ້າ",
  shop: "ຮ້ານຄ້າ",
  mechanic: "ຊ່າງ",
  platform: "ແພລດຟອມ",
};

const METHOD_LABELS: Record<string, string> = {
  bank_transfer: "ໂອນທະນາຄານ",
  cash: "ເງິນສົດ",
};

function PayoutStatsCards() {
  const { data: stats, isLoading } = usePayoutStatsQuery();

  if (isLoading) {
    return (
      <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-[100px] rounded-xl" />
        ))}
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <SummaryCard
        title="ລໍຖ້າອະນຸມັດ (ລວມ)"
        icon={WalletIcon}
        color="orange"
        items={[{ label: "", value: formatPrice(stats.pendingTotal, "LAK") }]}
        subtitle="ຍອດ requested + approved"
        subtitleClassName="text-muted-foreground"
      />
      <SummaryCard
        title="ກຳລັງດຳເນີນການ"
        icon={LoaderIcon}
        color={stats.processingCount > 0 ? "primary" : "muted"}
        items={[{ label: "", value: stats.processingCount.toLocaleString() }]}
        subtitle="ລາຍການ processing"
        subtitleClassName="text-muted-foreground"
      />
      <SummaryCard
        title="ຈ່າຍແລ້ວ (ມື້ນີ້)"
        icon={BanknoteIcon}
        color="success"
        items={[{ label: "", value: formatPrice(stats.paidToday, "LAK") }]}
        subtitle="ຍອດທີ່ paid ມື້ນີ້"
        subtitleClassName="text-muted-foreground"
      />
      <SummaryCard
        title="ອັດຕາລົ້ມເຫຼວ"
        icon={AlertTriangleIcon}
        color={stats.failureRate > 0 ? "destructive" : "muted"}
        items={[{ label: "", value: `${stats.failureRate}%` }]}
        subtitle="failed / (paid + failed)"
        subtitleClassName="text-muted-foreground"
      />
    </div>
  );
}

export function PayoutsPage() {
  const nav = useNavigate({ from: "/app/wallet/payouts" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/wallet/payouts",
  });

  const list = usePayoutListQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const statusFilter = findCondition(filters, "status");
  const methodFilter = findCondition(filters, "payoutMethod");

  const statusValue = (statusFilter?.value as string) || "all";
  const methodValue = (methodFilter?.value as string) || "all";
  const hasFilter = statusValue !== "all" || methodValue !== "all";

  function handleFilterChange(field: string, value: string) {
    let next: FilterConditionDTO[] | undefined = filters;
    if (value === "all") {
      next = removeConditions(filters, field);
    } else {
      next = upsertCondition(filters, { field, op: "eq", value });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next?.length ? next : undefined,
      },
    });
  }

  const columns: TanstackReactTable.ColumnDef<PayoutRequestItem>[] = [
    createSortableColumn<PayoutRequestItem>("createdAt", "ວັນທີ", {
      size: 170,
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">
          {formatDateTime(row.original.createdAt)}
        </span>
      ),
    }),
    {
      id: "owner",
      header: "ເຈົ້າຂອງ",
      cell: ({ row }) => {
        const r = row.original;
        return (
          <div className="min-w-0">
            <div className="truncate font-medium text-sm">
              {r.ownerName ?? "—"}
            </div>
            <div className="text-muted-foreground text-xs">
              {OWNER_TYPE_LABELS[r.ownerType] ?? r.ownerType}
            </div>
          </div>
        );
      },
      size: 200,
    },
    {
      id: "amount",
      header: "ຈຳນວນເງິນ",
      cell: ({ row }) => (
        <span className="font-semibold text-destructive tabular-nums">
          -{formatPrice(row.original.amount, row.original.currency)}
        </span>
      ),
      size: 160,
    },
    {
      id: "payoutMethod",
      header: "ວິທີຈ່າຍ",
      cell: ({ row }) => (
        <span className="text-sm">
          {METHOD_LABELS[row.original.payoutMethod] ??
            row.original.payoutMethod}
        </span>
      ),
      size: 140,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => {
        const s = row.original.status;
        const style = STATUS_STYLE[s] ?? {
          dot: "bg-muted-foreground",
          text: "text-muted-foreground",
          label: s,
        };
        return (
          <div className="flex items-center gap-2">
            <span
              className={`size-2 shrink-0 rounded-full ${style.dot}`}
              aria-hidden
            />
            <span className={`text-sm ${style.text}`}>{style.label}</span>
          </div>
        );
      },
      size: 160,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <RowActions
          actions={[
            {
              label: "ເບິ່ງລາຍລະອຽດ",
              icon: <EyeIcon className="size-4" />,
              onClick: () =>
                nav({
                  to: "/app/wallet/payouts/$id",
                  params: { id: row.original.id },
                }),
            },
          ]}
        />
      ),
      size: 60,
    },
  ];

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ກະເປົາເງິນ & ການຈ່າຍເງິນ", to: "/app/wallet" },
            { label: "ການຈ່າຍເງິນອອກ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-2 flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="font-bold text-2xl tracking-tight">
                ການຈ່າຍເງິນອອກ
              </h2>
              <p className="text-muted-foreground">
                ລາຍການຄຳຂໍຈ່າຍເງິນ ອະນຸມັດ ແລະ ດຳເນີນການຈ່າຍ
              </p>
            </div>
            <Button
              onClick={() => nav({ to: "/app/wallet/payouts/create" })}
              className="shrink-0"
            >
              <PlusIcon className="size-4" /> ສ້າງຄຳຂໍຈ່າຍເງິນ
            </Button>
          </div>
        </div>

        <PayoutStatsCards />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <div className="mb-4 flex flex-col gap-2 px-2">
            <div className="flex items-center justify-between gap-4 max-sm:flex-col">
              <div className="flex flex-1 flex-wrap items-center gap-2">
                <SimpleSelect
                  value={statusValue}
                  onValueChange={(val) => handleFilterChange("status", val)}
                  placeholder="ສະຖານະ"
                  className="h-8 w-full sm:w-44"
                  options={STATUS_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
                <SimpleSelect
                  value={methodValue}
                  onValueChange={(val) =>
                    handleFilterChange("payoutMethod", val)
                  }
                  placeholder="ວິທີຈ່າຍ"
                  className="h-8 w-full sm:w-40"
                  options={METHOD_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
              </div>
              {hasFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    nav({
                      search: { ...search, filters: undefined, offset: 0 },
                    })
                  }
                  className="max-sm:w-full"
                >
                  <XIcon className="size-4" /> ລ້າງ
                </Button>
              )}
            </div>
          </div>

          <DataTable<PayoutRequestItem, unknown>
            noDataMessage="ບໍ່ພົບຄຳຂໍຈ່າຍເງິນ"
            isLoading={list.isLoading}
            columns={columns}
            data={list.data?.data ?? []}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(p) =>
              nav({ search: { ...search, offset: p.offset, limit: p.limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(sorting) => {
              if (sorting[0]?.id === "") return;
              nav({
                search: {
                  ...search,
                  sort: [
                    {
                      field: sorting[0]?.id as string,
                      dir: sorting[0]?.desc ? "desc" : "asc",
                    },
                  ],
                },
              });
            }}
          />
        </div>
      </Main>
    </>
  );
}
